import React from 'react';
import { X } from '@phosphor-icons/react';
import PropTypes from 'prop-types';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SortIcon from '../../assets/images/SortIcon.png';
import {
  Overlay,
  Modal,
  CloseButton,
  ModalTitle,
  FileCountText,
  FileListContainer,
  FileItemWrapper,
  FileNumber,
  FileItemBox,
  FileItemContent,
  FileItemName,
  FileItemSize,
  DragHandle,
  RemoveButton,
  AddFilesButton,
  Actions,
  CancelButton,
  CombineButton,
  OutputNameRow,
  OutputNameLabel,
  OutputNameInput,
} from './SelectedFilesModalStyled';

// Sortable file item component
function SortableFileItem({ id, idx, file, onRemove, listeners, attributes, isDragging }) {
  const formatFileSize = (size) => {
    if (!size) return '';
    if (typeof size === 'number') {
      if (size < 1024) return `${size}B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}kb`;
      return `${(size / (1024 * 1024)).toFixed(1)}MB`;
    }
    return size; // If it's already a string
  };

  return (
    <FileItemWrapper style={{ opacity: isDragging ? 0.7 : 1 }}>
      <FileNumber>{idx + 1}.</FileNumber>
      <FileItemBox>
        <FileItemContent>
          <FileItemName data-testid={`selected-file-name-${idx}`}>
            {file.title || file.name || 'Untitled'}
            {file.size && (
              <FileItemSize> ({formatFileSize(file.size)})</FileItemSize>
            )}
          </FileItemName>
          <DragHandle {...listeners} {...attributes}>
            <img src={SortIcon} alt="Sort" style={{ width: 20, height: 20, opacity: 0.7 }} />
          </DragHandle>
        </FileItemContent>
      </FileItemBox>
      <RemoveButton
        onClick={() => onRemove(idx)}
        aria-label="Remove file"
      >
        <X size={20} color="#5A6C87" />
      </RemoveButton>
    </FileItemWrapper>
  );
}

SortableFileItem.propTypes = {
  id: PropTypes.string.isRequired,
  idx: PropTypes.number.isRequired,
  file: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onRemove: PropTypes.func,
  listeners: PropTypes.object.isRequired,
  attributes: PropTypes.object.isRequired,
  isDragging: PropTypes.bool.isRequired,
};

const SortableFileItemWrapper = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: props.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1
  };
  
  return (
    <div ref={setNodeRef} style={style}>
      <SortableFileItem {...props} listeners={listeners} attributes={attributes} isDragging={isDragging} />
    </div>
  );
};

SortableFileItemWrapper.propTypes = {
  id: PropTypes.string.isRequired,
  ...SortableFileItem.propTypes
};

const SelectedFilesModal = ({
  open,
  onClose,
  selectedFiles = [],
  onRemoveFile,
  onAddFiles,
  onCombineFiles,
  onCancel,
  onReorder,
  outputName = '',
  onOutputNameChange,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Ensure each file has a unique id
  const filesWithIds = React.useMemo(() => {
    return selectedFiles.map((file, index) => ({
      ...file,
      id: file.id || `file-${index}-${Date.now()}`,
    }));
  }, [selectedFiles]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = filesWithIds.findIndex((f) => f.id === active.id);
      const newIndex = filesWithIds.findIndex((f) => f.id === over.id);
      const newOrder = arrayMove(filesWithIds, oldIndex, newIndex);
        onReorder(newOrder);
    }
  };

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal data-testid="selected-files-modal" onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          <X size={20} />
        </CloseButton>

        <ModalTitle>Selected Files to merge</ModalTitle>

        <FileCountText data-testid="selected-files-count">
          File(s) Selected - {selectedFiles.length}
        </FileCountText>

        <FileListContainer>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filesWithIds.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {filesWithIds.map((file, index) => (
                <SortableFileItemWrapper
                  key={file.id}
                  id={file.id}
                  idx={index}
                  file={file}
                  onRemove={onRemoveFile}
                />
              ))}
            </SortableContext>
          </DndContext>
        </FileListContainer>

        <AddFilesButton onClick={onAddFiles}>
          + Add Files
        </AddFilesButton>

        {onOutputNameChange && (
          <OutputNameRow>
            <OutputNameLabel htmlFor="merge-output-name">
              Merged file name (optional)
            </OutputNameLabel>
            <OutputNameInput
              id="merge-output-name"
              data-testid="merge-output-name"
              type="text"
              placeholder="e.g. STR Packet.pdf"
              value={outputName}
              maxLength={200}
              onChange={(e) => onOutputNameChange(e.target.value)}
            />
          </OutputNameRow>
        )}

        <Actions>
          <CancelButton onClick={onCancel || onClose}>Cancel</CancelButton>
          <CombineButton
            onClick={onCombineFiles}
            disabled={selectedFiles.length <= 1}
          >
            Combine Files
          </CombineButton>
        </Actions>
      </Modal>
    </Overlay>
  );
};

SelectedFilesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
      size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      id: PropTypes.string,
    })
  ),
  onRemoveFile: PropTypes.func,
  onAddFiles: PropTypes.func,
  onCombineFiles: PropTypes.func,
  onCancel: PropTypes.func,
  onReorder: PropTypes.func,
  outputName: PropTypes.string,
  onOutputNameChange: PropTypes.func,
};

export default SelectedFilesModal;
